import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { first, switchMap } from 'rxjs/operators';
import { AppConfig } from 'src/app/core/configs/app.config';
import { UserService } from '../../../core/user/user.service';
import { CampaignService } from '../../../services/campaign.service';
import { PaymentService } from '../../../services/payment.service';
import { UtilsService } from '../../../services/utils.service';
import { PublishSuccessModalComponent } from '../publish-success-modal/publish-success-modal.component';
import { CanComponentDeactivate } from '../guards/can-deactivate.interface';
import { checkIfModeExistsInRouteParams } from 'src/app/shared/utility/campaign';
import { LoadingOverlayService } from 'src/app/shared/loading-overlay/loading-overlay.service';

@Component({
    selector: 'app-complete-payment',
    templateUrl: './complete-payment.component.html',
    styleUrls: ['./complete-payment.component.scss']
})
export class CompletePaymentComponent implements OnInit, CanComponentDeactivate {
    public form: FormGroup;
    public countries = [];
    userBillingAddress = null;
    campaign;
    wallet;
    checkoutInfo;
    newCheckOutInfo;
    totalCampaignWithVAT = 0;
    saveBillingAddressDetails = false;
    editMode = false;
    previewMode = false;
    deactivateGuard = false;

    public constructor(
        private loadingOverlayService: LoadingOverlayService,
        private formBuilder: FormBuilder,
        public dialog: MatDialog,
        private campaignService: CampaignService,
        private route: ActivatedRoute,
        private paymentService: PaymentService,
        private userService: UserService,
        private utilsService: UtilsService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.editMode = checkIfModeExistsInRouteParams(this.route, 'edit-mode');
        this.previewMode = checkIfModeExistsInRouteParams(this.route, 'preview');
        this.getCampaign();
        this.getCountries();
        this.initForm();
    }

    canDeactivate(): boolean {
        return this.deactivateGuard;
    }

    async saveDraft(): Promise<boolean> {
        return Promise.resolve(true);
    }

    getCampaign() {
        let campaignId = this.route.snapshot.paramMap.get('id');
        if (campaignId != null) {
            this.campaignService.getById(campaignId).subscribe(campaign => {
                this.campaign = campaign;
                this.getCampaignDetails();
            });
        }
    }

    getCampaignDetails() {
        this.paymentService.getCheckoutInfo(this.campaign._id).subscribe((info: any) => {
            this.checkoutInfo = this.editMode ? info[1] : info[0];
            if (this.editMode) this.newCheckOutInfo = info[0];
            this.wallet = info[0].walletDTO;
            this.totalCampaignWithVAT = info[0].totalPriceWithVat.value;
            this.getUserBillingAddress();
        });
    }

    getUserBillingAddress() {
        this.userService
            .getUserBillingAddress()
            .pipe(first())
            .subscribe({
                next: resp => {
                    this.userBillingAddress = resp;
                    this.initForm(this.userBillingAddress);
                },
                error: error => {
                    this.initForm();
                }
            });
    }

    getCountries() {
        this.utilsService.getCountries().subscribe(resp => {
            this.countries = resp;
        });
    }

    get f() {
        return this.form.controls;
    }

    private initForm(userBillingAddress?): void {
        this.form = this.formBuilder.group({
            wallet_amount: new FormControl({
                value: this.totalCampaignWithVAT.toFixed(2),
                disabled: true
            }),
            saveBillingAddressDetails: new FormControl(userBillingAddress?.firstName !== undefined),
            firstName: new FormControl(
                userBillingAddress ? userBillingAddress.firstName : null,
                RxwebValidators.required({ message: 'First name is required.' })
            ),
            lastName: new FormControl(
                userBillingAddress ? userBillingAddress.lastName : null,
                RxwebValidators.required({ message: 'Last name is required.' })
            ),
            country: new FormControl(
                userBillingAddress ? userBillingAddress.country : null,
                RxwebValidators.required({
                    message: 'Country is required.'
                })
            ),
            city: new FormControl(
                userBillingAddress ? userBillingAddress.city : null,
                RxwebValidators.required({
                    message: 'City is required.'
                })
            ),
            street: new FormControl(
                userBillingAddress ? userBillingAddress.street : null,
                RxwebValidators.required({
                    message: 'Street is required.'
                })
            ),
            building: new FormControl(
                userBillingAddress ? userBillingAddress.building : null,
                RxwebValidators.required({
                    message: 'Building/Floor is required.'
                })
            ),
            postalCode: new FormControl(userBillingAddress ? userBillingAddress.postalCode : null)
        });
    }

    public publishSuccess() {
        this.deactivateGuard = true;
        const approvementPerSupplier = this.route.snapshot.paramMap.get('approvementPerSupplier');

        const saveBillingAddressDetails = this.form.get('saveBillingAddressDetails').value;

        this.loadingOverlayService.show();
        const updateAndCheckout = saveBillingAddressDetails
            ? this.userService.updateUserBillingAddress(this.form.value).pipe(
                  first(),
                  switchMap(() =>
                      this.paymentService.checkoutAndPublish(
                          this.campaign._id,
                          approvementPerSupplier,
                          this.previewMode
                      )
                  )
              )
            : this.paymentService.checkoutAndPublish(
                  this.campaign._id,
                  approvementPerSupplier,
                  this.previewMode
              );

        updateAndCheckout.subscribe((info: any) => {
            this.loadingOverlayService.hide();
            if (this.campaign) {
                this.campaign.beingEdited = false;
            }
            const dialogRef = this.dialog.open(PublishSuccessModalComponent, {
                width: AppConfig.dialog_width_xs,
                maxHeight: AppConfig.dialog_max_height,
                data: {}
            });
            dialogRef.afterClosed().subscribe(result => {
                this.router.navigateByUrl('/dashboard');
            });
        });
    }

    disableCheckout() {
        let checkOutWallet;
        if (this.checkoutInfo?.walletDTO == null) {
            checkOutWallet = this.newCheckOutInfo?.walletDTO;
        } else {
            checkOutWallet = this.checkoutInfo?.walletDTO;
        }
        return (
            !this.campaign?.channels?.length ||
            checkOutWallet?.amount < this.totalCampaignWithVAT ||
            (this.form.get('saveBillingAddressDetails').value && !this.form.valid)
        );
    }

    abs(value) {
        return Math.abs(value);
    }

    checkoutNow() {
        this.publishSuccess();
    }
}
